import { Avatar, Box, Typography, Slider } from "@mui/material";
import { memo, useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import Cropper from "react-easy-crop";

import { useAppContext } from "../../../../context/appContext";
import authService from "../../../../service/auth.service";
import imageServiceInstance from "../../../../service/image.service";
import KsbDialogActions from "../../../../components/KsbDialogActions";
import BoxHeader from "../../../../../3_apps/5_Husibuech/components/1_Boxes/BoxHeader";

function AccountProfile() {
  const theme = useTheme();
  const { currentUser, setCurrentUser } = useAppContext();

  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [roleNames, setRoleNames] = useState("");

  // ✅ crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // ✅ FILE SELECT
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    setFile(selectedFile);
    setImagePreview(URL.createObjectURL(selectedFile));
  };

  // ✅ AVATAR CLICK
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  // ✅ CLEAR
  const resetFile = () => {
    setFile(null);
    setImagePreview(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  // ✅ ROLE NAMES
  useEffect(() => {
    if (!currentUser?.roles) return;
    setRoleNames(currentUser.roles.join(", "));
  }, [currentUser]);

  // ✅ INITIALS
  const getInitials = (firstName, lastName) => {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`;
    if (firstName) return firstName[0];
    if (lastName) return lastName[0];
    return "?";
  };

  // ✅ IMAGE CROP HELPER
  const getCroppedImage = async (imageSrc, crop) => {
    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // ✅ LIMIT SIZE HERE
    const MAX_SIZE = 300; // avatar size (px)

    canvas.width = MAX_SIZE;
    canvas.height = MAX_SIZE;

    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      MAX_SIZE,
      MAX_SIZE,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.7, // ✅ compression quality (0–1)
      );
    });
  };

  // ✅ UPLOAD
  const handleUpload = async () => {
    if (!file || !currentUser?.id) return;

    try {
      setLoading(true);

      const croppedBlob = await getCroppedImage(
        imagePreview,
        croppedAreaPixels,
      );

      const croppedFile = new File([croppedBlob], "avatar.jpg", {
        type: "image/jpeg",
      });

      const response = await imageServiceInstance.upload(
        currentUser.id,
        croppedFile,
      );

      const updatedUser = {
        ...currentUser,
        avatar: response,
      };

      authService.editCurrentUser(updatedUser);
      setCurrentUser(updatedUser);

      resetFile();
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const sectionStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    p: 3,
    boxShadow: 2,
  };

  return (
    <Box sx={sectionStyle}>
      <BoxHeader title="Avatar ändern" />

      {/* ✅ HIDDEN INPUT */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg"
        onChange={handleFileSelect}
        hidden
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
        {/* ✅ AVATAR */}
        {!imagePreview && (
          <Avatar
            src={
              currentUser?.avatar
                ? `data:image/jpeg;base64,${currentUser.avatar}`
                : undefined
            }
            sx={{
              width: 110,
              height: 110,
              cursor: "pointer",
              fontSize: "2.5rem",
              bgcolor: theme.palette.primary.main,
            }}
            onClick={handleAvatarClick}
          >
            {getInitials(currentUser?.firstName, currentUser?.lastName)}
          </Avatar>
        )}

        {/* ✅ CROPPER */}
        {imagePreview && (
          <>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 300,
                height: 300,
              }}
            >
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(croppedArea, croppedPixels) =>
                  setCroppedAreaPixels(croppedPixels)
                }
              />
            </Box>

            {/* ✅ ZOOM */}
            <Slider
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e, value) => setZoom(value)}
            />
          </>
        )}

        {/* ✅ USER INFO */}
        <Box>
          <Typography variant="h6">
            {currentUser?.userNickName ||
              `${currentUser?.firstName} ${currentUser?.lastName}`}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.inactive }}
          >
            Funktion: {roleNames}
          </Typography>
        </Box>

        {/* ✅ ACTIONS */}
        {file && (
          <KsbDialogActions
            onCancel={resetFile}
            onConfirm={handleUpload}
            confirmLabel="Speichern"
            confirmDisabled={loading}
          />
        )}
      </Box>
    </Box>
  );
}

export default memo(AccountProfile);
